import { Observable } from "rxjs/Observable";
import * as actions from "./user.actions";
import { User } from "./user.model";
import { Membership } from "../membership/membership.model";
import { UserFilters } from "./user.reducer";
import { VasBaseObjectStoreFacade } from "../base-object/vas-base-object-store.facade";
import { ActionOptionsArgs } from "../../../core/store/actions/base-actions";
import { Qry } from "../query-facade";
import { Md5 } from "ts-md5";

export class UserStoreFacade extends VasBaseObjectStoreFacade<User> {

    getAll(): Observable<Array<User>> {
        return this.select(Qry.getInstance().user.items);
    }

    getFiltered(): Observable<Array<User>> {
        return this.select(Qry.getInstance().user.filtered);
    }

    getActive(): Observable<User> {
        return this.select(Qry.getInstance().user.selected);
    }

    setActive(user: User): void {
        return this.dispatch(new actions.SetAction(user.id));
    }

    deActivate(): void {
        return this.dispatch(new actions.SetAction(undefined));
    }

    create(user: User): void {
        this.dispatch(new actions.SubmitAction(user));
    }

    remove(user: User, options?: ActionOptionsArgs): void {
        this.dispatch(new actions.RemoveAction(user, options));
    }

    update(user: User, options?: ActionOptionsArgs): void {
        this.dispatch(new actions.SubmitAction(user));
    }

    updatePassword(user: User, password: string): void {

        if (!password) {
            return;
        }

        user = new User({
            ...user,
            passwordHash: Md5.hashStr(password).toString()
        });

        this.update(user, {persistable: true, restful: false});
    }

    insert(user: User): void {
        this.dispatch(new actions.SubmitAction(user, {restful: false, persistable: true}));
    }

    isOwner(user): Observable<boolean> {

        if (!user) {
            return Observable.of(false);
        }

        return this.appFacade.objects.account.store.getActive().map(account => account && account.owner === user.id);
    }

    isOwnerByAccountUser(account, user): Observable<boolean> {

        if (!account || !user) {
            return Observable.of(false);
        }

        return this.select(Qry.getInstance().helper.isAccountOwner(account.id));
    }

    isAdmin(user): Observable<boolean> {

        if (!user) {
            return Observable.of(false);
        }

        return Observable.combineLatest(
            this.appFacade.objects.membership.store.getAll().map(memberships => memberships.filter(membership => membership.user === user.id)),
            this.select(Qry.getInstance().account.selected)
        ).map(args => {
            const memberships: Array<Membership> = args[0];
            const account: Account = args[1];

            if (!memberships || !account) {
                return false;
            }

            const membership = memberships.find(m => m.account === account.id);
            return membership ? membership.admin : false;
        });
    }

    isAdminByAccountUser(account, user): Observable<boolean> {

        if (!account || !user) {
            return Observable.of(false);
        }

        return this.select(Qry.getInstance().membership.isAdminByAccountUser(account.id, user.id));
    }

    isActiveUserAdmin(): Observable<boolean> {
        return this.getActive().concatMap(user => this.isAdmin(user));
    }

    isActiveUserOwner(): Observable<boolean> {
        return this.getActive().concatMap(user => this.isOwner(user));
    }

    isActiveUserAdminOrOwner(): Observable<boolean> {
        return Observable.zip(
            this.isActiveUserAdmin(),
            this.isActiveUserOwner()
        ).map(args => args[0] || args[1]);
    }

    setFilters(filters: UserFilters): void {
        this.dispatch(new actions.SetFilterAction(filters));
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/user/user.store-facade.ts